/**
 * <TutorStudentVideo> — la vidéo d'un élève du live, vue du masteur (molecule).
 *
 * Une `<VideoTile>` (sa caméra, son prénom dessus, son visage caméra coupée, estompé quand il n'est pas
 * là), sa puce la plus urgente au coin (`<TutorStudentChips>`), et le liseré vert pendant qu'il parle.
 * Deux tailles, celles de la tuile : `tile` dans la pellicule, `stage` en grand dans l'onglet Visio.
 * Elle remplit son conteneur.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveParticipant } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { TutorStudentChips } from "../TutorStudentChips"
import { VideoTile } from "../VideoTile"

export type TutorStudentVideoProps = {
    student: UITutorLiveStudent
    /** `tile` (défaut) : la pellicule ; `stage` : l'onglet Visio. */
    size?: "tile" | "stage"
    /** Sa vraie caméra (`VideoView` de LiveKit). Absente : sa photo, ou son visage caméra coupée. */
    video?: React.ReactNode
    /** Sa puce en glyphe seul (une carte réduite, `<TutorStudentChips compact>`). Défaut `false`. */
    compactChip?: boolean
    testID?: string
}

/**
 * Ce que sa vidéo dit de lui. La main ne s'y pose pas : la puce la dit. Le micro coupé, si l'écran le sait
 * (`student.micOff`, l'ancien micro barré à côté du nom) ; absent, rien. Sa connexion faible, les barres de l'ancienne
 * tuile.
 */
export function tutorStudentParticipant(student: UITutorLiveStudent): UILiveParticipant {
    return {
        id: student.id,
        name: student.firstname,
        avatarUrl: student.avatarUrl,
        cameraOff: student.cameraOff === true,
        micOff: student.micOff === true,
        poorConnection: student.poorConnection === true,
    }
}

/** Ma vidéo : une vraie piste l'emporte sur la caméra marquée coupée, sinon ma capsule. */
export function tutorMeParticipant(me: UILiveParticipant, video: React.ReactNode): UILiveParticipant {
    return { ...me, cameraOff: video == null && me.cameraOff }
}

const SPEAKING_RING = 3
/**
 * Le visage caméra coupée, dans la pellicule et la visio du centre : plus petit que celui de la tuile, l'initiale ne
 * prend pas la vidéo (daily du 04/11, « problème récurrent »).
 */
const FACE_SIZE = { tile: 28, stage: 64 } as const

export function TutorStudentVideo(props: TutorStudentVideoProps) {
    const { student, size = "tile", video, compactChip = false, testID } = props
    const here = student.presence === "here"
    return (
        <View style={styles.root} testID={testID}>
            <VideoTile
                participant={tutorStudentParticipant(student)}
                size={size}
                video={video}
                absent={!here}
                micMark="name"
                faceSize={FACE_SIZE[size]}
            />
            <View style={styles.chip(size)} pointerEvents="none">
                <TutorStudentChips student={student} compact={compactChip} />
            </View>
            {student.speaking === true && here && <View style={styles.speaking(size)} pointerEvents="none" />}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, alignSelf: "stretch" },
    chip: (size: "tile" | "stage") => ({
        position: "absolute" as const,
        top: theme.spacing[size === "stage" ? "3" : "1"],
        left: theme.spacing[size === "stage" ? "3" : "1"],
    }),
    /** Il parle : le liseré vert, posé dans la vidéo, à son rayon. */
    speaking: (size: "tile" | "stage") => ({
        position: "absolute" as const,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderRadius: size === "stage" ? theme.radius.lg : theme.radius.md,
        borderWidth: SPEAKING_RING,
        borderColor: theme.colors.success,
    }),
}))
